import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import dagre from 'dagre';
import { useGetSuppliers } from '../hooks/useGetSuppliers';
import type { UserOrganisation, Supplier } from '../types/types';
import {
  Tab,
  Table,
  Form,
  Button,
  Modal,
  Row,
  Col,
  Pagination,
  Badge,
  Container,
  Spinner,
  Tabs,
} from 'react-bootstrap';
import { useState, useEffect, useMemo, type JSX } from 'react';
import { useDeleteSuppliers } from '../hooks/useDeleteSupplier';
import { useNavigate, useLocation } from 'react-router-dom';
import { getCountryNameFromIso } from '../util/countryUtil';
import SupplierDetails from '../pages/SupplierDetails';
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  type Edge,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

const MAP_NODE_WIDTH = 240;
const MAP_NODE_HEIGHT = 96;
const MAP_RANK_SEPARATION = 120;
const MAP_NODE_SEPARATION = 48;
const MAP_GRID_COLUMNS = 5;
const MAP_GRID_HORIZONTAL_GAP = 36;
const MAP_GRID_VERTICAL_GAP = 36;

type SupplierMapNode = Node<{ label: JSX.Element }>;
type SupplierMapEdge = Edge;

const buildSupplierMapGraph = (suppliers: Supplier[]) => {
  const graph = new dagre.graphlib.Graph();
  graph.setDefaultEdgeLabel(() => ({}));
  graph.setGraph({
    rankdir: 'TB',
    ranksep: MAP_RANK_SEPARATION,
    nodesep: MAP_NODE_SEPARATION,
    marginx: 24,
    marginy: 24,
  });

  const validSuppliers = suppliers.filter(
    (supplier): supplier is NonNullable<Supplier> & { id: string } =>
      Boolean(supplier?.id),
  );
  const supplierIds = new Set(validSuppliers.map((supplier) => supplier.id));
  const childSupplierIds = new Set<string>();
  const parentSupplierIds = new Set<string>();
  const standaloneSupplierIds: string[] = [];

  validSuppliers.forEach((supplier) => {
    const validParentIds = (supplier.parent_ids ?? []).filter(
      (parentSupplierId) => supplierIds.has(parentSupplierId),
    );

    if (validParentIds.length > 0) {
      childSupplierIds.add(supplier.id);
    }

    validParentIds.forEach((parentSupplierId) => {
      parentSupplierIds.add(parentSupplierId);
    });
  });

  validSuppliers.forEach((supplier) => {
    if (
      !childSupplierIds.has(supplier.id) &&
      !parentSupplierIds.has(supplier.id)
    ) {
      standaloneSupplierIds.push(supplier.id);
    }

    graph.setNode(supplier.id, {
      width: MAP_NODE_WIDTH,
      height: MAP_NODE_HEIGHT,
    });
  });

  const relationshipIds = new Set<string>();

  validSuppliers.forEach((supplier) => {
    (supplier.parent_ids ?? [])
      .filter((parentSupplierId) => supplierIds.has(parentSupplierId))
      .forEach((parentSupplierId) => {
        const relationshipId = `${parentSupplierId}-${supplier.id}`;

        if (relationshipIds.has(relationshipId)) {
          return;
        }

        relationshipIds.add(relationshipId);
        graph.setEdge(parentSupplierId, supplier.id);
      });
  });

  dagre.layout(graph);

  const nodes: SupplierMapNode[] = validSuppliers.map((supplier) => {
    const dagreNode = graph.node(supplier.id);
    const countryName = getCountryNameFromIso(supplier.country) || 'Unknown';
    const shouldUseGridLayout =
      !childSupplierIds.has(supplier.id) && !parentSupplierIds.has(supplier.id);
    const standaloneIndex = standaloneSupplierIds.findIndex(
      (id) => id === supplier.id,
    );
    const gridColumn = standaloneIndex % MAP_GRID_COLUMNS;
    const gridRow = Math.floor(standaloneIndex / MAP_GRID_COLUMNS);
    const fallbackPosition = {
      x: gridColumn * (MAP_NODE_WIDTH + MAP_GRID_HORIZONTAL_GAP),
      y: gridRow * (MAP_NODE_HEIGHT + MAP_GRID_VERTICAL_GAP),
    };

    return {
      id: supplier.id,
      position:
        shouldUseGridLayout || !dagreNode
          ? fallbackPosition
          : {
              x: dagreNode.x - MAP_NODE_WIDTH / 2,
              y: dagreNode.y - MAP_NODE_HEIGHT / 2,
            },
      data: {
        label: (
          <div>
            <div className="fw-semibold text-dark small text-truncate">
              {supplier.company_name}
            </div>
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
              {supplier.registration_number || 'No registration number'}
            </div>
            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
              {countryName}
            </div>
          </div>
        ),
      },
      style: {
        width: MAP_NODE_WIDTH,
        minHeight: MAP_NODE_HEIGHT,
        borderRadius: '18px',
        border: '1px solid #dbe4ee',
        background: '#ffffff',
        boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)',
        padding: '0.85rem 1rem',
      },
    };
  });

  const edges: SupplierMapEdge[] = Array.from(relationshipIds).map(
    (relationshipId) => {
      const [source, target] = relationshipId.split('-');

      return {
        id: relationshipId,
        source,
        target,
        type: 'smoothstep',
        style: {
          stroke: '#94a3b8',
          strokeWidth: 2,
        },
      };
    },
  );

  return { nodes, edges };
};

function useQueryParams() {
  return new URLSearchParams(useLocation().search);
}

export default function SupplierList({ reloadKey }: { reloadKey: number }) {
  const navigate = useNavigate();
  const location = useLocation();
  const query = useQueryParams();

  const selectedOrganisation = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (state: any) => state.organisation.selected,
  ) as UserOrganisation | undefined;

  const [search, setSearch] = useState(query.get('search') || '');
  const [page, setPage] = useState(Number(query.get('page') || 1));
  const [sortBy, setSortBy] = useState<
    'company_name' | 'registration_number' | 'updated_at'
  >(
    (query.get('sortBy') as
      | 'company_name'
      | 'registration_number'
      | 'updated_at') || 'updated_at',
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    (query.get('sortDirection') as 'asc' | 'desc') || 'desc',
  );

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSupplierDetailsModal, setShowSupplierDetailsModal] =
    useState(false);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(
    null,
  );
  const [localReloadKey, setLocalReloadKey] = useState(0);
  const [activeTab, setActiveTab] = useState<'list' | 'map'>('list');

  const pageSize = 10;

  const { deleteSuppliers, loading: deleting } = useDeleteSuppliers();

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (page > 1) params.set('page', String(page));
    if (sortBy) params.set('sortBy', sortBy);
    if (sortDirection) params.set('sortDirection', sortDirection);

    navigate(
      {
        pathname: location.pathname,
        search: params.toString(),
      },
      { replace: true },
    );
  }, [location.pathname, navigate, search, page, sortBy, sortDirection]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      if (search) {
        setPage(1);
      }
    }, 500);

    return () => clearTimeout(handler);
  }, [search]);

  const effectiveReloadKey = reloadKey + localReloadKey;

  const { suppliers, total, loading, error } = useGetSuppliers(
    selectedOrganisation?.organisation?.id ?? '',
    {
      page,
      pageSize,
      search: debouncedSearch,
      sortBy,
      sortDirection,
    },
    effectiveReloadKey,
  );

  const supplierMap = useMemo(
    () => buildSupplierMapGraph(suppliers),
    [suppliers],
  );

  if (!selectedOrganisation?.organisation) {
    return <div>Please select an organisation to see suppliers.</div>;
  }

  const buyerOrgId = selectedOrganisation.organisation.id;

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const openSupplierDetailsModal = (supplierId: string) => {
    setSelectedSupplierId(supplierId);
    setShowSupplierDetailsModal(true);
  };

  const confirmDelete = async () => {
    const success = await deleteSuppliers(selectedIds, {
      buyerOrgId,
      deleteContacts: true,
    });
    if (success) {
      setShowDeleteModal(false);
      setSelectedIds([]);
      setLocalReloadKey((prev) => prev + 1);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const handleSort = (
    column: 'company_name' | 'registration_number' | 'updated_at',
  ) => {
    if (sortBy === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (
    column: 'company_name' | 'registration_number' | 'updated_at',
  ) => {
    if (sortBy !== column) return null;
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <div>
      <style>
        {`
          .supplier-list-table > :not(caption) > * > * {
            padding-top: 0.9rem;
            padding-bottom: 0.9rem;
            vertical-align: middle;
          }

          .supplier-list-table > tbody > tr > td:not(:first-child) {
            border-left-width: 0;
          }

          .supplier-list-table > tbody > tr > td:not(:last-child) {
            border-right-width: 0;
          }

          .supplier-list-table > thead > tr > th:not(:first-child) {
            border-left-width: 0;
          }

          .supplier-list-table > thead > tr > th:not(:last-child) {
            border-right-width: 0;
          }

          .supplier-list-table th:first-child,
          .supplier-list-table td:first-child {
            width: 56px;
            text-align: center;
          }

          .supplier-list-checkbox {
            margin-bottom: 0;
          }

          .supplier-list-checkbox .form-check-input {
            width: 1.2rem;
            height: 1.2rem;
            margin-top: 0;
            border-radius: 0.4rem;
            border: 1px solid #b8c2cc;
            background-color: #f8fafc;
            box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.06);
            transition:
              background-color 0.15s ease,
              border-color 0.15s ease,
              box-shadow 0.15s ease,
              transform 0.15s ease;
          }

          .supplier-list-checkbox .form-check-input:hover {
            border-color: #7c8da1;
            background-color: #eef3f8;
          }

          .supplier-list-checkbox .form-check-input:focus {
            border-color: #0d6efd;
            box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.18);
          }

          .supplier-list-checkbox .form-check-input:checked {
            background-color: #0d6efd;
            border-color: #0d6efd;
          }

          .supplier-list-checkbox .form-check-input:checked[type='checkbox'] {
            background-size: 0.75rem;
          }

          .supplier-list-subtext {
            font-size: 0.85rem;
            color: #6c757d;
            line-height: 1.3;
          }

          .supplier-list-subtext-badges {
            display: inline-flex;
            flex-wrap: wrap;
            gap: 0.25rem;
            margin-left: 0.4rem;
            vertical-align: middle;
          }

          .supplier-list-subtext-badges .badge {
            font-size: 0.7rem;
            font-weight: 500;
            padding: 0.2rem 0.4rem;
          }
        `}
      </style>
      {loading && (
        <Container fluid className="d-flex">
          <Row className="justify-content-center align-self-center w-100">
            <Col className="text-center">
              <Spinner animation="border" role="status" variant="primary" />
            </Col>
          </Row>
        </Container>
      )}
      {error && <div>Error loading suppliers: {error}</div>}
      {!loading && !error && (
        <Tabs
          activeKey={activeTab}
          onSelect={(key) => setActiveTab((key as 'list' | 'map') ?? 'list')}
          className="mb-3"
          mountOnEnter
          unmountOnExit
        >
          <Tab
            eventKey="list"
            title={
              <>
                <i className="bi bi-list-ul me-2"></i>
                List
              </>
            }
          >
            <div className="border border-bottom-0 rounded-top-3 rounded-bottom-0 bg-white p-3 mt-3">
              <Row className="g-3 align-items-end">
                <Col md="auto">
                  <Button
                    variant="danger"
                    onClick={() => setShowDeleteModal(true)}
                    disabled={selectedIds.length === 0 || deleting}
                  >
                    {selectedIds.length > 0
                      ? `Delete (${selectedIds.length})`
                      : 'Delete'}
                  </Button>
                </Col>
                <Col md={5} className="ms-md-auto">
                  <Form.Group className="mb-0" controlId="supplierSearch">
                    <Form.Control
                      type="text"
                      placeholder="Search suppliers..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {suppliers.length === 0 ? (
              <div>No suppliers found.</div>
            ) : (
              <>
                <Table
                  bordered
                  hover
                  responsive
                  className="mb-0 supplier-list-table"
                >
                  <thead>
                    <tr>
                      <th>
                        <Form.Check
                          type="checkbox"
                          className="supplier-list-checkbox"
                          checked={
                            selectedIds.length === suppliers.length &&
                            suppliers.length > 0
                          }
                          onChange={(e) =>
                            setSelectedIds(
                              e.target.checked
                                ? suppliers
                                    .filter(
                                      (s) => s !== null && s!.id !== undefined,
                                    )
                                    .map((s) => s!.id as string)
                                : [],
                            )
                          }
                        />
                      </th>
                      <th
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSort('company_name')}
                      >
                        Supplier {renderSortIcon('company_name')}
                      </th>
                      <th>Country</th>
                      <th
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSort('updated_at')}
                      >
                        Updated {renderSortIcon('updated_at')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {suppliers.map((supplier) => {
                      const supplierId = supplier?.id;

                      if (!supplier || !supplierId) {
                        return null;
                      }

                      return (
                        <tr
                          key={supplierId}
                          style={{ cursor: 'pointer' }}
                          onClick={() => openSupplierDetailsModal(supplierId)}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <Form.Check
                              type="checkbox"
                              className="supplier-list-checkbox mt-1"
                              checked={selectedIds.includes(supplierId)}
                              onChange={() => toggleSelection(supplierId)}
                            />
                          </td>
                          <td>
                            <div>{supplier.company_name}</div>
                            <div className="supplier-list-subtext">
                              {supplier.registration_number || '-'}
                              {supplier.flags.length > 0 && (
                                <span className="supplier-list-subtext-badges">
                                  {supplier.flags.map((flag) => (
                                    <Badge
                                      bg={
                                        flag === 'duplicate'
                                          ? 'warning'
                                          : flag === 'incomplete'
                                            ? 'danger'
                                            : flag === 'review'
                                              ? 'info'
                                              : flag === 'imported'
                                                ? 'primary'
                                                : 'light'
                                      }
                                      key={flag}
                                    >
                                      {flag}
                                    </Badge>
                                  ))}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            {getCountryNameFromIso(supplier.country) || '-'}
                          </td>
                          <td>
                            {dayjs(supplier.updated_at).format(
                              'DD.MM.YYYY HH:mm',
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                {totalPages > 1 && (
                  <div
                    className="border rounded-bottom-3 rounded-top-0 bg-white p-3"
                    style={{ borderTop: 0 }}
                  >
                    <Pagination className="justify-content-center mb-0">
                      <Pagination.First
                        disabled={page === 1}
                        onClick={() => setPage(1)}
                      />
                      <Pagination.Prev
                        disabled={page === 1}
                        onClick={() => setPage((p) => Math.max(p - 1, 1))}
                      />
                      {[...Array(totalPages)].map((_, i) => (
                        <Pagination.Item
                          key={i + 1}
                          active={i + 1 === page}
                          onClick={() => setPage(i + 1)}
                        >
                          {i + 1}
                        </Pagination.Item>
                      ))}
                      <Pagination.Next
                        disabled={page === totalPages}
                        onClick={() =>
                          setPage((p) => Math.min(p + 1, totalPages))
                        }
                      />
                      <Pagination.Last
                        disabled={page === totalPages}
                        onClick={() => setPage(totalPages)}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </Tab>

          <Tab
            eventKey="map"
            title={
              <>
                <i className="bi bi-diagram-3 me-2"></i>
                Map
              </>
            }
          >
            <div className="border rounded-4 bg-white shadow-sm p-3 mb-3 mt-3">
              <Row className="g-3 align-items-center">
                <Col>
                  <div className="fw-semibold">Supplier Relationship Map</div>
                  <div className="text-muted small">
                    Click a supplier node to open its details.
                  </div>
                </Col>
                <Col md="auto">
                  <Badge bg="light" text="dark" className="border">
                    {supplierMap.nodes.length} suppliers
                  </Badge>
                </Col>
              </Row>
            </div>

            {suppliers.length === 0 ? (
              <div>No suppliers found.</div>
            ) : (
              <div
                className="border rounded-4 bg-white shadow-sm overflow-hidden"
                style={{ height: '620px' }}
              >
                <ReactFlow
                  nodes={supplierMap.nodes}
                  edges={supplierMap.edges}
                  fitView
                  fitViewOptions={{ padding: 0.2, maxZoom: 1, minZoom: 0.35 }}
                  nodesDraggable={false}
                  nodesConnectable={false}
                  elementsSelectable={true}
                  onNodeClick={(_, node) => {
                    openSupplierDetailsModal(node.id);
                  }}
                >
                  <Controls />
                  <MiniMap pannable zoomable />
                  <Background
                    variant={BackgroundVariant.Dots}
                    gap={12}
                    size={1}
                  />
                </ReactFlow>
              </div>
            )}
          </Tab>
        </Tabs>
      )}

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete {selectedIds.length} supplier
            {selectedIds.length > 1 ? 's' : ''} and all of their assessments?
          </p>
          <p className="text-danger">You cannot undo this operation</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Confirm'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showSupplierDetailsModal}
        onHide={() => {
          setShowSupplierDetailsModal(false);
          setSelectedSupplierId(null);
        }}
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Supplier Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSupplierId ? (
            <SupplierDetails supplierId={selectedSupplierId} embedded />
          ) : null}
        </Modal.Body>
      </Modal>
    </div>
  );
}
